import { isRecord, isTuple } from '@fmnts/core';
import {
  Duration,
  DurationCompareOptions,
  TimeSpanInput,
  greaterOrEqualToDuration,
  lessOrEqualToDuration,
} from '@fmnts/core/chronos';
import moment, { MomentInput } from 'moment';
import {
  ConditionBase,
  ConditionBuilder,
  ConditionFn,
} from '../condition.type';

type TimeSpanOptions = Duration & DurationCompareOptions;
type TimeSpanValue = TimeSpanInput | moment.MomentInput;

export interface ConditionTimespan extends ConditionBase {
  /** When `true` the timespan is in respect to the current datetime */
  readonly now?: boolean;
  readonly timespan_min?: TimeSpanOptions;
  readonly timespan_max?: TimeSpanOptions;
}

/**
 * Type guard that checks if an object fulfills the `ConditionTimespan` interface
 *
 * @param c Non nullish object
 * @returns `true` if `c` satisfies `ConditionTimespan`
 */
export function isConditionTimespan(
  c: Record<string, any>,
): c is ConditionTimespan {
  return (
    isRecord(c) && (isRecord(c['timespan_max']) || isRecord(c['timespan_min']))
  );
}

/**
 * @param condition Timespan condition
 *
 * @returns
 * Condition function that is called with a timespan (or single date) and
 * that returns `true` if the timespan is within the specified
 * duration range.
 * Otherwise, or when called without a timespan, returns `false`.
 */
export const timespan: ConditionBuilder<ConditionTimespan, TimeSpanValue> = (
  condition,
) => {
  const { timespan_min, timespan_max } = condition;
  const checks: ConditionFn[] = [];

  if (timespan_max) {
    checks.push(timespanMax(timespan_max));
  }
  if (timespan_min) {
    checks.push(timespanMin(timespan_min));
  }

  return (value) => {
    // Parse to timespan, if not a timespan yet
    const ts =
      Array.isArray(value) && isTuple(value, 2)
        ? value
        : timespanFromValue(value, condition);

    return ts ? checks.every((fn) => fn(ts)) : false;
  };
};

const timespanMin: ConditionBuilder<TimeSpanOptions, TimeSpanInput> = (
  opts,
) => {
  const { granularity = 'days', ...duration } = opts;
  return greaterOrEqualToDuration(duration, { granularity });
};

const timespanMax: ConditionBuilder<TimeSpanOptions, TimeSpanInput> = (
  opts,
) => {
  const { granularity = 'days', ...duration } = opts;
  return lessOrEqualToDuration(duration, { granularity });
};

function timespanFromValue(
  value: unknown,
  condition: ConditionTimespan,
): TimeSpanInput | null {
  const valueAsMoment = moment(value as MomentInput, true);
  if (!valueAsMoment.isValid()) {
    return null;
  }

  const other = getReferenceValue(condition);
  if (!other.isValid()) {
    console.warn('could not parse reference value from', condition);
    return null;
  }

  return [valueAsMoment, other];
}

function getReferenceValue(d: ConditionTimespan): moment.Moment {
  if (d.now) {
    return moment(new Date(), true);
  }

  return moment.invalid();
}
